import React from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { ClientAppConfig } from '../../../appsConfig';
import BonusHeader from './BonusHeader';
import { useBonusContext } from '../../../contexts/BonusContext';
import BettingIcon from '../../BettingIcon';
import { BonusItem } from './BonusItem';
import { BrandButton } from './BrandButton';
import PlayupLogo from './logo.png';
import PlayupLogoGrey from './logo-grey.png';
import DraftstarsLogo from './logo-draftstars.png';
import DraftstarsLogoGrey from './logo-draftstars-grey.png';
import Ticket from './ticket.png';
import { useRewards } from './useRewards';
import { dispatchPushUrl } from '../../../utils/eventDispatchers';

interface Props {
	activeApp: ClientAppConfig;
	isSmallScreen: boolean;
	toggleMenu: () => void;
}

const BonusMenu = ({ activeApp, isSmallScreen, toggleMenu }: Props) => {
	const history = useHistory();
	const { updateActiveBrand, refreshClientUserBonuses, activeBrand, draftstarsRewards } = useBonusContext();
	const {
		wageringRewardCounts,
		promotionsBonusList,
		legacyPromotionsBonusList,
		boostUpBonusList,
		ticketList,
		isNoRewards,
	} = useRewards();

	return (
		<MenuWrapper $fullScreen={isSmallScreen}>
			<Header $isFantasy={activeApp.clientId === 2}>My Rewards</Header>
			<BrandContainer>
				<BrandButton
					onClick={() => {
						updateActiveBrand('Betting');
						refreshClientUserBonuses('Betting');
					}}
					source={activeBrand === 'Betting' ? PlayupLogo : PlayupLogoGrey}
					aspectRatio={3}
					bonusCount={wageringRewardCounts}
					active={activeBrand === 'Betting'}
				/>
				<BrandButton
					onClick={() => {
						updateActiveBrand('Fantasy');
						refreshClientUserBonuses('Fantasy');
					}}
					bonusCount={draftstarsRewards.length}
					source={activeBrand === 'Fantasy' ? DraftstarsLogo : DraftstarsLogoGrey}
					aspectRatio={5.951}
					active={activeBrand === 'Fantasy'}
				/>
			</BrandContainer>
			<BonusWrapper>
				{promotionsBonusList.length + legacyPromotionsBonusList.length > 0 && (
					<>
						<BonusHeader
							icon={<BettingIcon color={'black'} icon={'bonus-cash'} width={14} height={11} fill={'black'} />}
							title={'Deposit Bonus'}
							subTitle={
								'View your available deposit offers. Once you’ve selected your deposit offer, simply deposit the corresponding amount to receive your bonus.'
							}
						/>

						{promotionsBonusList.map((item) => (
							<BonusItem
								onClick={() => {
									toggleMenu();
									history.push(`?modal=deposit`);
								}}
								subTitle="1 X Deposit Bonus"
								expiredAt={
									item.attributes.promotion.rules?.never_expire ? undefined : item.attributes.promotion.expired_at
								}
								key={`promotions-${item.id}`}
								activeBrand={activeBrand}
								buttonTitle={`Deposit Now`}
								type="promotions"
								name={item.attributes.promotion.name}
							/>
						))}

						{/* remove legacyPromotionsBonusList once backend has been released */}
						{legacyPromotionsBonusList.map((item) => (
							<BonusItem
								onClick={() => {
									toggleMenu();
									history.push(`?modal=deposit`);
								}}
								subTitle="1 X Deposit Bonus"
								expiredAt={item.attributes.rules?.never_expire ? undefined : item.attributes.expired_at}
								key={`promotions-${item.id}`}
								activeBrand={activeBrand}
								buttonTitle={`Deposit Now`}
								type="promotions"
								name={item.attributes.name}
							/>
						))}
					</>
				)}
				{activeBrand === 'Betting' && boostUpBonusList.length > 0 && (
					<>
						<BonusHeader
							icon={<BettingIcon icon="boost-up" width={14} height={11} fill={'black'} />}
							title={'BoostUp'}
							subTitle={
								'View your available BoostUp offers. Get better odds and potential returns on events of your choice.'
							}
						/>
						{boostUpBonusList.map((item) => (
							<BonusItem
								smallSubTitle
								onClick={() => {
									toggleMenu();
									const url = `/betting/${item.attributes.bet_category?.id === 1 ? 'racing' : 'sports'}`;
									history.replace(url);
									dispatchPushUrl(url);
								}}
								subTitle={`${
									item.attributes.bet_category?.id === 1
										? `Boosted odds on the runner of your choice. \nFirst single fixed odds win bet only. Max 1 boost per race.`
										: `Boosted odds on your favourite sports markets. \nSingles only. Market must have 4 or more betting options.`
								}`}
								key={`boosts-${item.id}`}
								buttonTitle={`Go To ${item.attributes.bet_category?.name}`}
								type="boosts"
								activeBrand={activeBrand}
								iconId={item.attributes.bet_category?.id}
								category={item.attributes.bet_category?.name?.toLowerCase()}
								name={`${item.attributes.name} (${item.attributes.active_count ?? 0})`}
							/>
						))}
					</>
				)}
				{activeBrand === 'Fantasy' && ticketList.length > 0 && (
					<>
						<BonusHeader
							icon={<TicketImg src={Ticket} />}
							title={'Ticket Entry'}
							subTitle={
								'View your available ticket(s) into future Draftstars contests. Take note of the contest that your ticket has been credited. Simply claim your ticket but entering your lineup and using the ticket for entry.'
							}
						/>
						{ticketList.map((item) => (
							<BonusItem
								onClick={() => {
									toggleMenu();
									const url = `/fantasy/contest/${item.attributes.challenge?.id}`;
									dispatchPushUrl(url);
									history.replace(url);
								}}
								balance={item.attributes.balance}
								subTitle={`1 x Entry Ticket`}
								key={`bonus-${item.id}`}
								buttonTitle={`Enter Now`}
								type="bonus"
								activeBrand={activeBrand}
								iconId={item.attributes.bonus_type?.id}
								category={'sport'}
								name={item?.attributes?.challenge?.name}
							/>
						))}
					</>
				)}
				{isNoRewards && <NoRewards />}
			</BonusWrapper>
		</MenuWrapper>
	);
};
const NoRewards = () => {
	return (
		<NoRewardsWrapper>
			<IconWrapper>
				<BettingIcon icon={'rewards'} color={'#727272'} width={85} height={85} />
			</IconWrapper>
			No Rewards available right now
			<NoRewardsText>
				You currently do not have any available rewards at the moment, but keep an eye on this section for future
				updates on exclusive perks, promotions, and exciting rewards!
			</NoRewardsText>
		</NoRewardsWrapper>
	);
};
export default BonusMenu;

const TicketImg = styled.img({
	width: 22,
	height: 18,
});
const NoRewardsWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	color: '#191D22',
	fontWeight: 'bold',
	fontSize: 16,
	paddingTop: 48,
});
const NoRewardsText = styled.div({
	textAlign: 'center',
	backgroundColor: '#DEDEDE',
	paddingLeft: 22,
	paddingRight: 22,
	paddingTop: 12,
	paddingBottom: 12,
	fontSize: 11,
	marginTop: 10,
	fontWeight: 'normal',
});
const IconWrapper = styled.div({
	width: 141,
	height: 141,
	borderRadius: 70.5,
	backgroundColor: '#DEDEDE',
	justifyContent: 'center',
	alignItems: 'center',
	display: 'flex',
	marginBottom: 25,
});
const BonusWrapper = styled.div(({ theme }) => ({
	backgroundColor: '#EEEEEE',
	paddingLeft: 20,
	paddingRight: 20,
	paddingBottom: 22,
	height: '100%',
}));
const Header = styled.div<{ $isFantasy: boolean }>(({ $isFantasy, theme }) => ({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	backgroundColor: $isFantasy ? theme.colors.fantasyPrimary : theme.colors.primary,
	color: $isFantasy ? theme.colors.white : theme.colors.black,
	width: '100%',
	height: '38px',
	fontWeight: 'bold',
	fontSize: 14,
}));
const BrandContainer = styled.div({
	backgroundColor: 'white',
	height: 68,
	width: '100%',
	flexDirection: 'row',
	display: 'flex',
	alignItems: 'center',
});
const MenuWrapper = styled.div<{ $fullScreen: boolean }>`
	position: ${({ $fullScreen }) => ($fullScreen ? 'fixed' : 'absolute')};
	width: ${({ $fullScreen }) => ($fullScreen ? '100%' : '400px')};
	right: 0;
	height: ${({ $fullScreen }) => ($fullScreen ? '100%' : 'auto')};
	color: ${({ theme }) => theme.colors.darkGrey};
	background-color: ${({ theme }) => theme.colors.white};
	z-index: 61;
	margin-top: 10px;
	border-radius: 4px;
	overflow: hidden;
`;
